import { QueryClient } from '@tanstack/react-query';
import { isApiError, ErrorCode } from '../types/auth.types';
import { logError, ErrorLevel } from './utils/error.utils';

/**
 * Custom retry logic for queries
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  // Don't retry on authentication errors
  if (isApiError(error)) {
    if (
      error.code === ErrorCode.AUTH_TOKEN_INVALID ||
      error.code === ErrorCode.AUTH_TOKEN_EXPIRED ||
      error.code === ErrorCode.AUTH_UNAUTHORIZED
    ) {
      return false;
    }
    
    // Don't retry on validation errors
    if (error.code === ErrorCode.VALIDATION_ERROR) {
      return false;
    }
    
    // Don't retry on 4xx errors (except 429 - rate limit)
    if (error.statusCode >= 400 && error.statusCode < 500 && error.statusCode !== 429) {
      return false;
    }
  }
  
  // Retry up to 2 times for network and server errors
  return failureCount < 2;
}

/**
 * Global error handler for queries
 */
function onError(error: unknown): void {
  // Log all query errors
  logError(error, ErrorLevel.ERROR, {
    source: 'react-query',
  });
  
  // Additional handling for specific error types can be added here
  // For example, showing a global error toast for critical errors
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: shouldRetry,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false, // Don't retry mutations by default
      onError, // Global error handler for mutations
    },
  },
});
